import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiPlus, FiSend, FiEdit2, FiTrash2, FiTrendingUp, FiGitPullRequest } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import {
  getMyGoalSheet, createGoal, updateGoal, deleteGoal, submitMyGoalSheet, updateGoalProgress, getMyGoalSuggestions,
  requestGoalChange, getGoalChangeRequests, getGoalProgressHistory,
} from "../../services/goalApi";
import { getLookups } from "../../services/employeeApi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Button, Loader, ErrorState, ConfirmDialog, Banner, DataTable, StatusBadge } from "../../components/common";
import WeightageMeter from "../../components/goals/WeightageMeter";
import GoalList from "../../components/goals/GoalList";
import GoalFormDrawer from "../../components/goals/GoalFormDrawer";
import GoalProgressModal from "../../components/goals/GoalProgressModal";
import { listProgressApprovals, isPendingApproval } from "../../services/progressApprovalApi";
import ActivityTimeline from "../../components/goals/ActivityTimeline";
import OwnershipPanel from "../../components/goals/OwnershipPanel";
import RoleSuggestionsCard from "../../components/goals/RoleSuggestionsCard";
import GoalQuota from "../../components/goals/GoalQuota";
import KraGoalExplainer from "../../components/goals/KraGoalExplainer";
import { APPROVED_GOAL_STATUSES, EDITABLE_GOAL_STATUSES, GOAL_STATUS, SHEET_STATUS, MAX_GOALS_PER_YEAR, goalLimitMessage } from "../../utils/constants";
import { getErrorMessage } from "../../utils/errors";
import { formatDate } from "../../utils/format";

const api = {
  getMyGoalSheet, createGoal, updateGoal, deleteGoal, submitMyGoalSheet, updateGoalProgress, getLookups, getMyGoalSuggestions,
  requestGoalChange, getGoalChangeRequests, getGoalProgressHistory,
};


const CHANGE_TONE = { PENDING: "warning", APPROVED: "success", REJECTED: "danger", CANCELLED: "neutral" };

export default function MyGoalsPage() {
  const { cycleId, cycle } = useCycle();
  const { profile } = useRole();
  const sheet = useAsync(() => api.getMyGoalSheet(cycleId), [cycleId], { skip: !cycleId });
  const lookups = useAsync(() => api.getLookups(), []);
  const suggestions = useAsync(() => api.getMyGoalSuggestions(cycleId), [cycleId], { skip: !cycleId });
  const recordId = sheet.data?.recordId;
  const changes = useAsync(() => api.getGoalChangeRequests(recordId), [recordId], { skip: !recordId });
  const [changing, setChanging] = useState(null);
  const pendingProgress = useAsync(() => listProgressApprovals({ view: "mine", status: "PENDING", recordId }), [recordId], { skip: !recordId });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [progressGoal, setProgressGoal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [busy, setBusy] = useState(false);

  const data = sheet.data;
  const editable = useMemo(
    () => Boolean(data?.stageOpen) && [SHEET_STATUS.NOT_STARTED, SHEET_STATUS.DRAFT, SHEET_STATUS.CHANGES_REQUESTED].includes(data?.sheetStatus),
    [data]
  );

  if (sheet.error) return <ErrorState message={sheet.error} onRetry={sheet.reload} />;
  if (!data) return <Loader />;

  const remaining = Math.round((100 - data.totalWeightage + (editing ? Number(editing.weightage) : 0)) * 100) / 100;
  const lastChange = data.history.find((h) => h.action === "Changes requested" || h.action === "Reopened by HR");

  const save = async (form) => {
    try {
      if (editing) await api.updateGoal(editing.id, { ...form, cycleId });
      else await api.createGoal({ ...form, cycleId });
      toast.success(editing ? "Goal updated." : "Goal added.");
      sheet.reload();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    }
  };

  const saveChangeRequest = async (form) => {
    try {
      await api.requestGoalChange(changing.id, form, form.employeeComments || "Goal change requested");
      toast.success("Change request sent to your manager.");
      changes.reload();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    }
  };

  const saveProgress = async (payload) => {
    try {
      const { data: saved } = await api.updateGoalProgress(progressGoal.id, payload);
      toast.success(isPendingApproval(saved) ? "Progress sent to your manager for approval. The goal updates once it is approved." : "Progress saved.");
      pendingProgress.reload();
      sheet.reload();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteGoal(deleting.id);
      toast.success("Goal deleted.");
      setDeleting(null);
      sheet.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.submitMyGoalSheet(cycleId);
      toast.success(`Goals submitted to ${profile.manager?.name || "your manager"}.`);
      setConfirmSubmit(false);
      sheet.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = editable && data.goals.length > 0 && data.totalWeightage === 100 && !data.weightIssues.length;
  const canRequestChange = data.trackingOpen && data.selfStatus !== "Submitted" && cycle?.status === "Active";

  const goalStage = (cycle?.stages || []).find((st) => st.key === "goalSetting");
  const closedReason = (() => {
    if (cycle?.status === "Draft") {
      return { title: "The cycle has not been launched yet", text: "HR must launch this cycle (Performance cycles → Launch) before goals can be added." };
    }
    if (goalStage?.status === "upcoming") {
      return { title: `Goal setting opens on ${formatDate(goalStage.startDate)}`, text: `You can add goals from ${formatDate(goalStage.startDate)} to ${formatDate(goalStage.endDate)}. HR sets these dates on the cycle.` };
    }
    if (data.extension && new Date(data.extension.closesAt) <= new Date()) {
      return { title: `Your extension ended on ${formatDate(data.extension.closesAt)}`, text: "Ask HR for another Deadline override if you still need to add goals." };
    }
    if (data.extension && new Date(data.extension.opensAt) > new Date()) {
      return { title: `Your extension starts on ${formatDate(data.extension.opensAt)}`, text: `HR gave you extra time until ${formatDate(data.extension.closesAt)}.` };
    }
    if (goalStage?.status === "closed") {
      return { title: `Goal setting closed on ${formatDate(goalStage.endDate)}`, text: "Ask HR for a Deadline override (Review & finalize → your record) if you still need to add goals." };
    }
    return { title: "Goal setting is not open for you", text: "Your record has moved past the goal-setting stage. Contact HR if you still need to change goals." };
  })();
  const canAdd = editable && data.canSelfCreate;

  const usage = data.goalUsage;
  const limitBlocked = () => {
    if (!usage?.limitReached) return false;
    toast.error(goalLimitMessage(usage.yearLabel));
    return true;
  };

  const openBlank = () => {
    if (limitBlocked()) return;
    setEditing(null); setPrefill(null); setFormOpen(true);
  };

  const pickSuggestion = (item) => {
    if (limitBlocked()) return;
    setEditing(null);
    setPrefill({ ...item, dueDate: cycle?.endDate || "" });
    setFormOpen(true);
  };

  const addAllSuggestions = async (items) => {
    if (limitBlocked()) return false;
    if (usage && items.length > usage.remaining) {
      toast.error(`Only ${usage.remaining} more goal${usage.remaining === 1 ? "" : "s"} can be added for ${usage.yearLabel} (maximum ${MAX_GOALS_PER_YEAR} goals per year). Pick suggestions one by one with "Use".`);
      return false;
    }
    try {
      for (const item of items) {
        await api.createGoal({ ...item, dueDate: cycle?.endDate || "", employeeComments: "", cycleId });
      }
      toast.success(`${items.length} goal${items.length === 1 ? "" : "s"} added from your role template.`);
      sheet.reload();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      sheet.reload();
      return false;
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="My goals"
        subtitle={`${cycle?.name}. You add your own goals; ${profile.manager?.name || "your reporting manager"} approves the sheet.`}
        actions={
          editable && (
            <>
              {canAdd && <Button variant="secondary" icon={FiPlus} onClick={openBlank}>Add goal</Button>}
              <Button icon={FiSend} disabled={!canSubmit} onClick={() => setConfirmSubmit(true)} title={canSubmit ? undefined : data.weightIssues.length ? "HR must fix the KRA/KPI weights first" : "Goal percentages must total 100%"}>
                Submit for approval
              </Button>
            </>
          )
        }
      />

      <KraGoalExplainer view="employee" />

      {data.sheetStatus === SHEET_STATUS.CHANGES_REQUESTED && (
        <Banner tone="warning" title="Your manager asked for changes">
          {lastChange?.comment || "Check the comments on each goal, update them and submit again."}
        </Banner>
      )}
      {data.sheetStatus === SHEET_STATUS.PENDING && (
        <Banner tone="info" title="Waiting for approval">
          Your goals are with {profile.manager?.name || "your manager"}. You can't edit them until they're approved or sent back.
        </Banner>
      )}
      {(pendingProgress.data || []).length > 0 && (
        <Banner tone="info" title={`${pendingProgress.data.length} progress update${pendingProgress.data.length === 1 ? "" : "s"} waiting for approval`}>
          {pendingProgress.data.map((x) => `${x.itemTitle} (${x.progress ?? "—"}%)`).join(", ")}. The goals change once {profile.manager?.name || "your manager"} approves.
        </Banner>
      )}
      {data.sheetStatus === SHEET_STATUS.APPROVED && !data.resultFinalized && (
        <Banner tone="success" title="Goals approved">
          Goal definitions are locked. Keep your progress updated so your self assessment is quick.
        </Banner>
      )}
      {data.resultFinalized && (
        <Banner tone="neutral" title="Appraisal finalized">This cycle is closed for you. Goals are read-only.</Banner>
      )}
      {!data.stageOpen && [SHEET_STATUS.NOT_STARTED, SHEET_STATUS.DRAFT, SHEET_STATUS.CHANGES_REQUESTED].includes(data.sheetStatus) && (
        <Banner tone="warning" title={closedReason.title}>{closedReason.text}</Banner>
      )}

      {data.stageOpen && data.extension && (
        <Banner tone="info" title={`HR extended your goal setting until ${formatDate(data.extension.closesAt)}`}>
          The cycle deadline has passed, but you have extra time to finish your goals.
        </Banner>
      )}
      {data.weightIssues.length > 0 && (
        <Banner tone="warning" title="Your KRA / KPI weights need fixing by HR">
          {data.weightIssues.join(" ")} You can&apos;t submit until HR corrects them (otherwise your review could not be finished at the end).
        </Banner>
      )}

      {editable && !data.canSelfCreate && (
        <Banner tone="warning" title="You can't add goals yourself">
          Your role doesn't have the goal.self.create permission. Ask HR / Admin to grant it (Users & roles).
        </Banner>
      )}
      {/* [REMOVED] Requirement 5: no "goals given by your manager" notice; you create all your goals yourself. */}

      {usage?.limitReached && editable && (
        <Banner tone="danger" title="Goal limit reached">
          {goalLimitMessage(usage.yearLabel)}
        </Banner>
      )}

      <Card title="Goals this year" subtitle={`You can set at most ${MAX_GOALS_PER_YEAR} goals per year.`}>
        <GoalQuota usage={usage} />
      </Card>

      <Card title="Goal percentage" subtitle="Each goal carries a goal percentage. All goals together must add up to exactly 100% before you submit.">
        <WeightageMeter goals={data.goals} />
      </Card>

      <RoleSuggestionsCard
        suggestion={suggestions.data}
        goals={data.goals}
        editable={canAdd}
        onUse={pickSuggestion}
        onAddAll={addAllSuggestions}
      />

      <Card
        title="Who owns what"
        subtitle={`You add your goals; ${profile.manager?.name || "your manager"} approves them and can correct them with a reason.`}
      >
        <OwnershipPanel view="employee" />
      </Card>

      <Card title={`Goals (${data.goals.length})`} subtitle={usage ? `${usage.used} of ${MAX_GOALS_PER_YEAR} goals used for ${usage.yearLabel}` : undefined} padded={false}>
        <GoalList
          goals={data.goals}
          emptyTitle="No goals added yet"
          emptyText={`Add your goals for this cycle with Add goal. When they total 100%, submit them to ${profile.manager?.name || "your manager"} for approval.`}
          emptyAction={canAdd && <Button variant="secondary" icon={FiPlus} onClick={openBlank}>Add goal</Button>}
          renderActions={(goal) => {
            if (editable && EDITABLE_GOAL_STATUSES.includes(goal.status)) {
              return (
                <>
                  <Button variant="ghost" size="sm" icon={FiEdit2} aria-label={`Edit ${goal.title}`} onClick={() => { setPrefill(null); setEditing(goal); setFormOpen(true); }} />
                  <Button variant="ghost" size="sm" icon={FiTrash2} aria-label={`Delete ${goal.title}`} onClick={() => setDeleting(goal)} />
                </>
              );
            }
            if (APPROVED_GOAL_STATUSES.includes(goal.status) && goal.status !== GOAL_STATUS.CLOSED && !data.resultFinalized) {
              return (
                <>
                  {data.trackingOpen && <Button variant="secondary" size="sm" icon={FiTrendingUp} onClick={() => setProgressGoal(goal)}>Update</Button>}
                  {canRequestChange && <Button variant="ghost" size="sm" icon={FiGitPullRequest} aria-label={`Request a change to ${goal.title}`} title="Request a change" onClick={() => setChanging(goal)} />}
                </>
              );
            }
            return null;
          }}
        />
      </Card>

      {(changes.data || []).length > 0 && (
        <Card title="Change requests" subtitle="Changes to approved goals need your manager's decision." padded={false}>
          <DataTable
            dense
            rows={changes.data}
            columns={[
              { key: "goal", label: "Goal", render: (r) => <span className="cell-stack"><strong>{r.goalTitle}</strong><small>{r.reason}</small></span> },
              { key: "proposed", label: "Proposed", render: (r) => `${r.proposed.weight ?? "—"}% weight, target ${r.proposed.target ?? "—"}` },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} tone={CHANGE_TONE[r.status]} /> },
              { key: "decision", label: "Decision note", render: (r) => r.decisionReason || "—" },
            ]}
          />
        </Card>
      )}

      <Card title="Approval history">
        <ActivityTimeline items={data.history} emptyText="Submissions and approvals will be listed here." />
      </Card>

      <GoalFormDrawer
        open={formOpen}
        goal={editing}
        initial={prefill}
        cycle={cycle}
        categories={lookups.data?.goalCategories}
        remaining={remaining}
        goalUsage={usage}
        onClose={() => setFormOpen(false)}
        onSave={save}
      />
      <GoalProgressModal open={Boolean(progressGoal)} goal={progressGoal} onClose={() => setProgressGoal(null)} onSave={saveProgress} loadHistory={api.getGoalProgressHistory} />
      <GoalFormDrawer
        open={Boolean(changing)}
        goal={changing}
        title="Request a change"
        subtitle="Your manager must approve the change. Note: approving it sends your whole goal sheet back to draft, so you'll need to submit it for approval again (your progress history is kept)."
        submitLabel="Send change request"
        cycle={cycle}
        categories={lookups.data?.goalCategories}
        remaining={100}
        onClose={() => setChanging(null)}
        onSave={saveChangeRequest}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete this goal?"
        message={`"${deleting?.title}" will be removed from your sheet.`}
        confirmLabel="Delete goal"
        tone="danger"
        loading={busy}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      />
      <ConfirmDialog
        open={confirmSubmit}
        title="Submit goals for approval?"
        message={`${data.goals.length} goals totalling 100% will go to ${profile.manager?.name || "your manager"}. You can't edit them while they're under review.`}
        confirmLabel="Submit goals"
        loading={busy}
        onConfirm={submit}
        onClose={() => setConfirmSubmit(false)}
      />
    </div>
  );
}
